import DropFiles from "e.sic-platform.webclient/src/components/upload-drop-files/DropFiles";
import UploadTable from "e.sic-platform.webclient/src/components/upload-drop-files/UploadTable";
import { api } from "e.sic-platform.webclient/src/services/api/ApiService";
import { errorHandler } from "e.sic-platform.webclient/src/services/error-handler/ErrorHandlerService";
import {
  VideoAnalysisService,
  VideoAnalysisServiceModel,
} from "e.sic-platform.webclient/src/services/inspection/VideoAnalysisService";
import { translationService } from "e.sic-platform.webclient/src/services/translation/TranslationService";
import { uploadFilesService } from "e.sic-platform.webclient/src/services/upload/UploadFilesService";
import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Grid, Stack } from "@mui/material";
import VideoAnalysisDiagram from "e.sic-platform.webclient/src/pages/analysis/components/video-analysis-diagram/VideoAnalysisDiagram";
import DamagesVideoAnalysis from "e.sic-platform.webclient/src/pages/analysis/components/DamagesVideoAnalysis";
import { DamagesStorageModel } from "e.sic-platform.webclient/src/services/damage/DamagesStorage";
import { featureFlagsStorage } from "e.sic-platform.webclient/src/services/feature-flags/FeatureFlagsStorage";
import { Flags } from "e.sic-platform.webclient/src/generated/FeatureFlags";
import Tools from "e.sic-platform.webclient/src/pages/analysis/components/video-analysis-diagram/tools/Tools";
import { AnalysisConfidenceStorageModel } from "e.sic-platform.webclient/src/services/organization-analysis-confidence/AnalysisConfidenceStorage";
import useUrlQueryParams from "e.sic-platform.webclient/src/services/url/UrlQueryService";
import SmartApp, { useSmartApps } from "../../components/apps/SmartApp";
import SmartAppStep from "../../components/apps/SmartAppStep";
import VideoAnalysisPlayer from "../../components/video-player/VideoAnalysisPlayer";
import UploadStatus from "e.sic-platform.webclient/src/components/upload-status/UploadStatus";
import { FileUploadTracker } from "e.sic-platform.webclient/src/services/upload/FileUploadTracker";
import RunVideoAnalysisButton from "../../components/buttons/run-video-analysis-button/RunVideoAnalysisButton";
import PurchaseConfirmation from "../../components/apps/PurchaseConfirmation";
import { ActivityTypes } from "e.sic-platform.webclient/src/services/shop/ActivityPricesStorage";
import { creditBalanceStorage } from "e.sic-platform.webclient/src/services/shop/CreditBalanceStorage";
import CopyUrlButton from "../../components/buttons/copy-url-button/CopyUrlButton";
import LoadingSpinner from "e.sic-platform.webclient/src/components/loading-spinner/LoadingSpinner";

const emptyDamagesStorage = DamagesStorageModel.create({ inspectionId: 0 });
emptyDamagesStorage.mapResponse({ totalCount: 0, rows: [], header: [] });

export default observer(function VideoAnalysisPage() {
  return (
    <SmartApp
      titleKey="general.apps.video-analysis.title"
      descriptionKey="videoAnalysis.description"
      image="/images/video-analysis.png"
    >
      <Page />
    </SmartApp>
  );
});

const Page = observer(function VideoAnalysisPage() {
  useEffect(() => {
    uploadFilesService.clear();
  }, [])

  uploadFilesService.allowOnlySingleFile(true);
  const { setStep, confirmationPending, setConfirmationPending } =
    useSmartApps();
  const { id }: any = useUrlQueryParams();
  const [confidenceStorage] = useState(() =>
    AnalysisConfidenceStorageModel.create({ autoLoad: false }, { api })
  );
  const [videoAnalysis] = useState<VideoAnalysisService>(
    VideoAnalysisServiceModel.create({ videoId: id, language: translationService.language }, { api: api, errorHandler: errorHandler })
  );
  const canAnalyze = useMemo(
    () => uploadFilesService.filesForUpload.length !== 0,
    [uploadFilesService.filesForUpload.length]
  );

    const onUploadFinished = useCallback((file: FileUploadTracker) => {
        file.completed();
        videoAnalysis.setVideoId(file.id!);
        videoAnalysis.startVideoAnalysis();
        videoAnalysis.reload();
    }, [videoAnalysis])

    const onConversionFinished = useCallback(async (id:number) => {
        if (id != videoAnalysis.videoId) return;
        videoAnalysis.reload();
    }, [videoAnalysis]);

  const onVAFinished = useCallback(
    async (fileId: number) => {
      if (videoAnalysis.videoId != fileId) return;
      videoAnalysis.reload();
      await creditBalanceStorage.load();
    },
    [videoAnalysis.videoId, creditBalanceStorage]
  );

  const runVideoAnalysis = useCallback(async () => {
    uploadFilesService.startStandaloneUpload(
      onConversionFinished,
      onUploadFinished
    );
  }, [uploadFilesService, onConversionFinished, onUploadFinished]);

  if (videoAnalysis.videoId) setStep(2);
  if (videoAnalysis.isAnalyzed) setStep(3);

  if (videoAnalysis && videoAnalysis.isLoading) return <Grid item xs={12}><LoadingSpinner /></Grid>

  return (
    <>
      <SmartAppStep
        titleKey="videoAnalysis.upload.title"
        descriptionKey="videoAnalysis.upload.description"
        visible={videoAnalysis.videoId === undefined}
        step={1}
      >
        <Grid item xs={12} sm={12}>
          <DropFiles
            singleFile
            onDroppedFiles={uploadFilesService.addFilesForUpload}
          />
        </Grid>
        <Grid item xs={12} sm={12} className="centered">
          {canAnalyze && 
            <UploadTable
              files={uploadFilesService.filesForUpload}
              onRemoveFile={uploadFilesService.removeFile}
            />
          }
        </Grid>
        <Grid item xs={12} sm={12} className="centered">
          <Stack direction="row" alignItems="center">
            <RunVideoAnalysisButton
              disabled={!canAnalyze}
              onClick={() => setConfirmationPending(!confirmationPending)}
            />
            <PurchaseConfirmation
              app={ActivityTypes.VideoAnalysis}
              visible={confirmationPending}
              confirm={runVideoAnalysis}
              toggle={() => setConfirmationPending(!confirmationPending)}
            />
          </Stack>
        </Grid>
        <Grid item xs={12} sm={12}>
          {uploadFilesService.uploadProgressModalIsVisbile && <UploadStatus />}
        </Grid>
      </SmartAppStep>
      <SmartAppStep
        titleKey="videoAnalysis.viewVideo.title"
        descriptionKey="videoAnalysis.viewVideo.description"
        visible={videoAnalysis.video !== undefined}
        step={2}
      >
        <Grid item xs={8}>
          <VideoAnalysisPlayer videoAnalysis={videoAnalysis!} />
        </Grid>
      </SmartAppStep>
      <SmartAppStep
        titleKey="videoAnalysis.analysis.title"
        descriptionKey="videoAnalysis.analysis.description"
        visible={
          videoAnalysis.video !== undefined && videoAnalysis.video.isConverted
        }
        step={3}
      >
        <Grid item xs={8}>
          {featureFlagsStorage.isFeatureEnabled(
            Flags.Render_VA_Diagram_In_Platform
          ) && (
            <VideoAnalysisDiagram
              video={videoAnalysis?.video}
              damagesStorage={emptyDamagesStorage}
              videoAnalysis={videoAnalysis!}
              onVAFinished={onVAFinished}
              readonly
              canAnalyze={true}
            />
          )}
          {!featureFlagsStorage.isFeatureEnabled(
            Flags.Render_VA_Diagram_In_Platform
          ) && (
            <DamagesVideoAnalysis
              video={videoAnalysis?.video}
              damagesStorage={emptyDamagesStorage}
              videoAnalysis={videoAnalysis!}
              onVAFinished={onVAFinished}
              inspectionLocked={false}
            />
          )}
          {videoAnalysis.analysisFailed && <div style={{marginBottom: "400px"}}></div>}
        </Grid>
        <Grid
          item
          xs={12}
          lg={4}
          className="centered"
        >
          <div className="legends-container">
            {featureFlagsStorage.isFeatureEnabled(
              Flags.Render_VA_Diagram_In_Platform
            ) && (
              <Tools
                videoAnalysisService={videoAnalysis!}
                analysisConfidenceStorage={confidenceStorage}
              />
            )}
          </div>
        </Grid>
      </SmartAppStep>
      <Grid item xs={12}>
        {videoAnalysis.videoId !== undefined && <CopyUrlButton id={videoAnalysis.videoId} />}
      </Grid>
    </>
  );
});
